<template>
  <div v-if="articles.length > 0" class="articleSlider z-120 fixed inset-x-0 top-[53px] w-full bg-white shadow-lg" :class="widgetClass">
    <Swiper :settings="settings" @swiper="onSwiper">
      <SwiperSlide v-for="(item, index) in articles" :key="item._id">
        <InstagramWidgetArticle :article="item" :index="index" />
      </SwiperSlide>
    </Swiper>
  </div>
</template>
<script>
import { computed, defineComponent, onMounted, ref, useContext, useRoute } from '@nuxtjs/composition-api';
import { throttle } from 'lodash';
import Swiper from '@/modules/sliders/core/Swiper';
import SwiperSlide from '@/modules/sliders/core/SwiperSlide';
import { useTracking } from '@/modules/tracking/composables/useTracking';
import { instagramPages } from '~/modules/instagram/utils/instagramUtils';
import InstagramWidgetArticle from '~/modules/instagram/components/InstagramWidgetArticle';
import { getLegacyId } from '~/modules/common/utils/stringUtils';

export default defineComponent({
  components: { InstagramWidgetArticle, Swiper, SwiperSlide },
  setup() {
    const { $service } = useContext();
    const route = useRoute();
    const slug = route.value.params.slug;
    const legacyId = getLegacyId(slug);
    const articles = ref([]);
    const firstArticleDataLayerSent = ref(false);
    const impressionTimeout = ref(null);

    const settings = computed(() => ({
      slidesPerView: 1.1,
      speed: 150,
      loop: false,
      preloadImages: false,
      breakpoints: {
        640: {
          slidesPerView: 1,
        },
      },
    }));

    // Data Layer Impression & Click
    const { sendDataLayer } = useTracking();
    const sliderDataLayerGenerator = (item, position) =>
      sendDataLayer(
        {
          item,
          list: 'Haber Detay - Instagram Köprü - Sticky',
          position,
        },
        ['init']
      );

    const onSwiper = (swiper) => {
      swiper.on('slideChange', function () {
        // Data Layer
        const slideIndex = swiper.realIndex;
        const { initEvent: dataLayerSendEvent } = sliderDataLayerGenerator(articles.value[slideIndex], slideIndex + 1);
        dataLayerSendEvent({ preventDuplicate: true });
      });
      swiper.on('click', function () {
        clearTimeout(impressionTimeout.value);
        impressionTimeout.value = setTimeout(() => {
          const slideIndex = parseInt(swiper.clickedSlide.dataset.swiperSlideIndex);
          sliderDataLayerGenerator(articles.value[slideIndex], slideIndex + 1).clickEvent();
        }, 1000);
      });
    };

    const getArticles = async () => {
      const currentPageData = instagramPages.onedio;
      const { data } = await $service.instagram.getArticles(currentPageData.id, 1, 15);
      articles.value = data.filter((item) => item.legacyId.toString() !== legacyId);
    };

    // scroll case
    const widgetClass = ref('');
    const isVisibleIGWidget = ref(true);
    const pageYOffset = ref(0);

    const getScrollPercent = () => {
      const h = document.documentElement;
      const b = document.body;
      const st = 'scrollTop';
      const sh = 'scrollHeight';
      return ((h[st] || b[st]) / ((h[sh] || b[sh]) - h.clientHeight)) * 100;
    };

    const scrollListener = () => {
      window.onscroll = throttle(() => {
        const diff = window.pageYOffset - pageYOffset.value;
        if (diff > 0 && diff < 100) return;
        isVisibleIGWidget.value = diff < 0;
        pageYOffset.value = window.pageYOffset;
        const calculatedWidgetClass =
          (getScrollPercent() > 10 || getScrollPercent() < 3) && isVisibleIGWidget.value ? 'show-widget' : 'hide-widget';
        widgetClass.value = calculatedWidgetClass;
        if (!firstArticleDataLayerSent.value && calculatedWidgetClass === 'show-widget') {
          // Data Layer Impression
          const { initEvent: dataLayerSendEvent } = sliderDataLayerGenerator(articles.value[0], 1);
          dataLayerSendEvent();
          firstArticleDataLayerSent.value = true;
        }
      }, 300);
    };

    onMounted(async () => {
      await getArticles();
      scrollListener();
    });
    return { articles, onSwiper, widgetClass, settings };
  },
});
</script>

<style lang="scss">
.articleSlider {
  .swiper-slide {
    height: auto !important;
  }
}
.hide-widget {
  top: -120px;
  transition: top 0.5s;
}
.show-widget {
  top: 53px;
  transition: top 0.5s;
}
</style>
